import React, { useState } from "react";
import InfoCard from "./InfoCard";
import { ArrowLeft, Edit2, Copy, GitBranch, CalendarClock, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@shared/ui/button";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "@shared/components/PageHeader";
import { Tabs } from "@shared/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import OverviewTab from "./tabs/Overview";
import RatesIncludedTab from "./tabs/RatesIncluded";
import { ChargesTaxes } from "./tabs/ChargesTaxes";
import { Determinants } from "./tabs/Determinants";
import { Deposits } from "./tabs/Deposits";
import { Penalties } from "./tabs/Penalties";
import { LinkedConsumers } from "./tabs/LinkedConsumers";
import PlanVersionHistory from "./tabs/VersionHistory";
import { useClonePlan, useGetPlanDetail } from "../../hooks";
import { EditPlaneModal } from "./EditRateModal";
import { TimelineTab } from "@features/cx/shared/components/tabs/TimelineTab";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

export const PlanDetailLanding = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const clonePlanMutation = useClonePlan();
  const { data: planDetailResponse } = useGetPlanDetail(id);
  const remoteUtilityId = useRemoteUtilityId();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const planDetail = planDetailResponse?.result || planDetailResponse;

  const parseVersionNumber = (ver: any): number => {
    if (ver === null || ver === undefined) return 1.0;
    if (typeof ver === "number") return isNaN(ver) ? 1.0 : ver;
    const str = String(ver);
    const match = str.match(/(\d+(?:\.\d+)?)/);
    if (match) {
      const num = parseFloat(match[1]);
      return isNaN(num) ? 1.0 : num;
    }
    return 1.0;
  };

  const currentVersionNumber = parseVersionNumber(planDetail?.version);
  const nextVersion = (currentVersionNumber + 0.1).toFixed(1);

    const handleClone = async () => {
      if (!id) return;
      try {
        await clonePlanMutation.mutateAsync(id);
      } catch (error) {
        console.error("Failed to clone plan", error);
      }
    };

  const tabComponents = {


overview: {
  label: "Overview",
  shortLabel: "overview",
  component: <OverviewTab />,
},
ratesIncluded: {
  label: "Rates Included",
  shortLabel: "ratesIncluded",
  component: <RatesIncludedTab />,
},
chargesAndTaxes: {
  label: "Charges & Taxes",
  shortLabel: "chargesAndTaxes",
  component:<ChargesTaxes/>,
},
    deposit: {
      label: "Deposit",
      shortLabel: "deposit",
      component: <Deposits />,
    },
    penalties: {
      label: "Penalties",
      shortLabel: "penalties",
      component: <Penalties />,
    },
versionHistory: {
  label: "Version History",
  shortLabel: "versionHistory",
  component: <PlanVersionHistory/>,
},
'activity-log': {
      label: 'Activity Log',
      component: (
        <TimelineTab
          entityId={id}
          remoteUtilityId={remoteUtilityId}
        />
      ),
    },
    linkedConsumers: {
      label: "Linked Consumers",
      shortLabel: "linkedConsumers",
      component: <LinkedConsumers />,
    },
  };

  const urlMapping = {
    overview: "overview",
    ratesIncluded: "ratesIncluded",
    chargesAndTaxes: "chargesAndTaxes",
    deposits: "deposits",
    penalties: "penalties",
    versionHistory: "versionHistory",
    activityLog: "activityLog",
    linkedConsumers: "linkedConsumers",
  };
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 w-full">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/plan-tarrif/plan-tarrif-landing?tab=plans")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>

        <div className="flex-1">
          <PageHeader
            title="Plan Details"
            subtitle="View and manage plan details."
          >
            <Button 
              variant="outline" 
              className="flex items-center gap-2"
              onClick={() => setIsEditModalOpen(true)}
            >
              <Edit2 className="w-4 h-4" />
              Edit
            </Button>
            <Button 
              variant="outline" 
              className="flex items-center gap-2" 
              onClick={handleClone} 
              disabled={clonePlanMutation.isPending}
            >
              <Copy className="w-4 h-4" />
              {clonePlanMutation.isPending ? "Cloning..." : "Clone"}
            </Button>
          </PageHeader>
        </div>
      </div>

      <InfoCard />
      
      <div className="pt-4">
        <Tabs
          defaultValue="overview"
          urlMapping={urlMapping}
          tabComponents={tabComponents}
          tabsListFullWidth={true}
          tabsListClassName="grid grid-cols-8"
        />
      <EditPlaneModal 
        isOpen={isEditModalOpen} 
        onOpenChange={setIsEditModalOpen} 
        currentVersion={planDetail?.version || "1.0"} 
      />
    </div>
     </div>
  );
};